import { useScrollToTop } from "@/hooks/useScrollToTop";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";

const TermsConditions = () => {
  useScrollToTop();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Terms & Conditions | Aureum VII Trading"
        description="Terms and conditions for purchasing luxury smart toilets and bathroom technology from Aureum VII Trading. Sales terms, warranties, and legal information."
        keywords="terms and conditions, sales terms, warranty, legal terms, purchase agreement"
        url="/terms-conditions"
      />
      <Navigation />
      
      <main className="container mx-auto px-4 py-20 max-w-4xl">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">Terms & Conditions</h1>
        
        <Card className="p-8 space-y-8">
          <section>
            <p className="text-muted-foreground mb-6">
              Last Updated: {new Date().toLocaleDateString('en-GB')}
            </p>
            <p className="mb-6">
              Welcome to Aureum VII Trading. These Terms and Conditions ("Terms") govern your access to and 
              use of our website, products, and services. By accessing or using our website, you agree to be 
              bound by these Terms. If you do not agree, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">1. Company Information</h2>
            <p className="mb-2"><strong>Company Name:</strong> Aureum VII Trading</p>
            <p className="mb-2"><strong>Registered Office:</strong> Marbella, Andalusia, Spain</p>
            <p className="mb-2"><strong>Email:</strong> <a href="mailto:info@aureum7trading.com" className="text-primary hover:underline">info@aureum7trading.com</a></p>
            <p className="mb-2"><strong>Phone:</strong> +34 642 278 939</p>
            <p className="mb-4"><strong>Website:</strong> <a href="https://www.aureum7trading.com" className="text-primary hover:underline">aureum7trading.com</a></p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">2. Definitions</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>"Company," "We," "Us," "Our":</strong> Refers to Aureum VII Trading</li>
              <li><strong>"Customer," "You," "Your":</strong> Refers to the individual or entity purchasing our products</li>
              <li><strong>"Products":</strong> Smart toilets, bidets, bathroom fixtures, and related wellness technology</li>
              <li><strong>"Services":</strong> Installation support, customer service, technical support, and related services</li>
              <li><strong>"Website":</strong> www.aureum7trading.com and all associated pages</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">3. Use of Website</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">3.1 Eligibility</h3>
            <p className="mb-4">
              You must be at least 18 years old to make purchases. By using our website, you represent and 
              warrant that you have the legal capacity to enter into binding contracts.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">3.2 Account Registration</h3>
            <p className="mb-4">
              If you create an account, you are responsible for maintaining the confidentiality of your 
              account credentials and for all activities that occur under your account. You must immediately 
              notify us of any unauthorized use.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">3.3 Prohibited Uses</h3>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the website for any unlawful purpose</li>
              <li>Violate intellectual property rights</li>
              <li>Transmit viruses, malware, or harmful code</li>
              <li>Engage in data scraping or automated data collection</li>
              <li>Interfere with website security or functionality</li>
              <li>Impersonate another person or entity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">4. Products and Orders</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">4.1 Product Information</h3>
            <p className="mb-4">
              We strive to provide accurate product descriptions, images, and specifications. However, we do 
              not warrant that product descriptions or other content is error-free, complete, or current. 
              Colors and images may vary slightly from actual products due to display settings.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">4.2 Pricing</h3>
            <p className="mb-4">
              All prices are in Euros (EUR) and include VAT unless otherwise stated. Prices are subject to 
              change without notice. We reserve the right to correct pricing errors and cancel orders placed 
              with incorrect pricing.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">4.3 Order Acceptance</h3>
            <p className="mb-4">
              Your order constitutes an offer to purchase products. We reserve the right to accept or decline 
              orders for any reason. Order confirmation does not guarantee acceptance. We will notify you once 
              your order is accepted and processed.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">4.4 Payment</h3>
            <p className="mb-4">
              Payment is required at the time of order placement. We accept credit cards, debit cards, bank 
              transfers, and other payment methods as indicated on our website. All payments are processed 
              securely through third-party payment processors.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">4.5 Delivery and Shipping</h3>
            <p className="mb-4">
              Delivery times are estimates and not guaranteed. We are not liable for delays caused by shipping 
              carriers, customs, or force majeure events. Risk of loss and title pass to you upon delivery.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">5. Warranties</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">5.1 Product Warranties</h3>
            <p className="mb-4">
              All products are covered by the manufacturer's warranty as specified in product documentation. 
              Warranty periods vary by product (typically 2-5 years). Warranties cover defects in materials 
              and workmanship under normal use.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">5.2 Warranty Exclusions</h3>
            <p className="mb-4">Warranties do not cover:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Normal wear and tear</li>
              <li>Damage from improper installation, use, or maintenance</li>
              <li>Modifications or repairs by unauthorized persons</li>
              <li>Damage from external factors (water quality, power surges, accidents)</li>
              <li>Commercial or industrial use (unless specified)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">5.3 Disclaimer of Warranties</h3>
            <p className="mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, PRODUCTS ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY 
              KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, 
              FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">6. Installation and Use</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">6.1 Professional Installation</h3>
            <p className="mb-4">
              We strongly recommend professional installation by licensed plumbers. Improper installation may 
              void warranties and cause damage. We are not responsible for installation errors or costs.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">6.2 Compatibility</h3>
            <p className="mb-4">
              It is your responsibility to ensure products are compatible with your existing plumbing, 
              electrical systems, and bathroom configuration. We provide technical specifications and 
              installation guides to assist you.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">6.3 Safe Use</h3>
            <p className="mb-4">
              Always follow manufacturer instructions and safety guidelines. Misuse or failure to follow 
              instructions may result in injury, property damage, or voided warranties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">7. Intellectual Property</h2>
            <p className="mb-4">
              All content on our website, including text, images, logos, designs, and software, is the property 
              of Aureum VII Trading or our licensors and is protected by copyright, trademark, and other 
              intellectual property laws.
            </p>
            <p className="mb-4">
              You may not reproduce, distribute, modify, display, or create derivative works without our 
              express written permission. Product names, brand names, and trademarks are property of their 
              respective owners.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">8. Limitation of Liability</h2>
            <p className="mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, AUREUM VII TRADING SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF 
              PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF OUR PRODUCTS OR SERVICES.
            </p>
            <p className="mb-4">
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE PRODUCT GIVING RISE TO THE CLAIM.
            </p>
            <p className="mb-4">
              Some jurisdictions do not allow exclusion or limitation of liability, so the above limitations 
              may not apply to you. You may have additional rights under local consumer protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">9. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Aureum VII Trading and its officers, directors, 
              employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including 
              legal fees) arising out of your use of products or services, violation of these Terms, or 
              infringement of any third-party rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">10. Force Majeure</h2>
            <p>
              We are not liable for delays or failures to perform due to circumstances beyond our reasonable 
              control, including but not limited to natural disasters, war, strikes, pandemics, government 
              actions, supply chain disruptions, or telecommunications failures.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">11. Governing Law and Jurisdiction</h2>
            <p className="mb-4">
              These Terms are governed by the laws of Spain and the European Union. Any disputes arising from 
              these Terms or your use of our products and services shall be subject to the exclusive jurisdiction 
              of the courts of Andalusia, Spain.
            </p>
            <p className="mb-4">
              European Union consumers may also have the right to use the European Commission's Online Dispute 
              Resolution (ODR) platform: 
              <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                ec.europa.eu/consumers/odr
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">12. Severability</h2>
            <p>
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions 
              shall remain in full force and effect. The invalid provision shall be replaced with a valid 
              provision that most closely reflects the original intent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">13. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon 
              posting on our website. Your continued use of our services after changes constitutes acceptance of 
              the revised Terms. We recommend reviewing these Terms periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">14. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy and Return Policy, constitute the entire agreement 
              between you and Aureum VII Trading regarding your use of our products and services, superseding 
              any prior agreements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">15. Contact Information</h2>
            <p className="mb-4">
              For questions about these Terms, please contact us:
            </p>
            <ul className="space-y-2">
              <li><strong>Email:</strong> <a href="mailto:info@aureum7trading.com" className="text-primary hover:underline">info@aureum7trading.com</a></li>
              <li><strong>Phone:</strong> +34 642 278 939</li>
              <li><strong>WhatsApp:</strong> +34 642 278 939</li>
              <li><strong>Address:</strong> Aureum VII Trading, Marbella, Andalusia, Spain</li>
            </ul>
          </section>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default TermsConditions;
